import { MessageStatusEnum } from "@/lib/enum/phone-message-status.enum";
import { PhoneMessageTypeEnum } from "@/lib/enum/phone-message-type.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import {
  MrfiktivSendMessageResultViewModelGen,
  MrfiktivSendSmsDtoGen,
  MrfiktivSendWhatsAppDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import phoneMessageService from "@/services/shared/phoneMessageService";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { ActivityLogDataAccessLayer } from "@/store/modules/access-layers/activity-log-service.access-layer";
import { UserModule } from "@/store/modules/me-user.store";
import VueRouter from "vue-router";
import { ActivityLog } from "./activity-log.entity";
import { IReference, Reference } from "./reference.entity";
import { ITimestamp, Timestamp, TimestampDocument } from "./timestamp.entity";
import { PhoneMessageDataAccessLayer } from "@/store/modules/access-layers/phone-message.access-layer";

@IsFilterable
class PhoneMessageBase {
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.phoneMessage.id"
  })
  id = "";

  @FilterConfig({
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(PhoneMessageTypeEnum).map((v) => {
        return {
          text: `enums.PhoneMessageTypeEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    },
    displayName: "objects.phoneMessage.type"
  })
  type: PhoneMessageTypeEnum;

  @FilterConfig({
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(MessageStatusEnum).map((v) => {
        return {
          text: `enums.MessageStatusEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    },
    displayName: "objects.phoneMessage.status"
  })
  status: MessageStatusEnum;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.phoneMessage.to"
  })
  to: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.phoneMessage.identifier"
  })
  identifier: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.phoneMessage.body"
  })
  body: string;

  partnerId: string;
  messageId?: string;
  providerMessageId?: string;
  error?: string;
  refs: IReference[] | undefined = [];
  timestamp: ITimestamp;

  get preview() {
    return this.body.slice(0, 100);
  }

  constructor(message?: Partial<PhoneMessageBase>) {
    this.id = message?.id || "";
    this.type = message?.type || PhoneMessageTypeEnum.SMS;
    this.status = message?.status || MessageStatusEnum.QUEUED;
    this.partnerId = message?.partnerId || "";
    this.to = message?.to || "";
    this.identifier = message?.identifier || "";
    this.body = message?.body || "";
    this.messageId = message?.messageId;
    this.providerMessageId = message?.providerMessageId;
    this.error = message?.error;
    this.refs = Reference.filterDuplicates((message?.refs ?? []).map((ref) => new Reference(ref)));
    this.timestamp = new Timestamp(message?.timestamp as ITimestamp);
  }

  private mapFromSendResult(result: MrfiktivSendMessageResultViewModelGen) {
    this.status = result.success ? MessageStatusEnum.PROCESSED : MessageStatusEnum.ERROR;
    this.messageId = result.messageId;
    this.providerMessageId = result.providerMessageId;
    this.error = result.error;

    // Generate an ID based on the result
    if (result.messageId) {
      this.id = result.messageId;
    } else if (result.providerMessageId) {
      this.id = result.providerMessageId;
    } else {
      this.id = `${this.type}_${Date.now()}`;
    }
  }

  /**
   * Send an SMS message
   */
  async sendSms(): Promise<this> {
    if (this.type !== PhoneMessageTypeEnum.SMS) {
      throw new Error("Message type must be SMS to send SMS");
    }

    this.status = MessageStatusEnum.QUEUED;

    const smsData: MrfiktivSendSmsDtoGen = {
      ...(this.identifier ? { identifier: this.identifier } : {}),
      to: this.to,
      body: this.body,
      refs: this.refs
    };

    try {
      const result = await phoneMessageService.sendSms(this.partnerId, smsData);

      if (result) {
        this.mapFromSendResult(result);
      }

      PhoneMessageDataAccessLayer.set(this);
      await this.createActivityLog();

      return this;
    } catch (error) {
      this.status = MessageStatusEnum.ERROR;
      this.error = error instanceof Error ? error.message : "Unknown error";
      throw error;
    }
  }

  /**
   * Send a WhatsApp message
   */
  async sendWhatsApp(): Promise<this> {
    if (this.type !== PhoneMessageTypeEnum.WHATSAPP) {
      throw new Error("Message type must be WhatsApp to send WhatsApp message");
    }

    this.status = MessageStatusEnum.QUEUED;

    const whatsAppData: MrfiktivSendWhatsAppDtoGen = {
      identifier: this.identifier,
      to: this.to,
      body: this.body
    };

    try {
      const result = await phoneMessageService.sendWhatsApp(this.partnerId, whatsAppData);

      if (result) {
        this.mapFromSendResult(result);
      }

      PhoneMessageDataAccessLayer.set(this);
      await this.createActivityLog();

      return this;
    } catch (error) {
      this.status = MessageStatusEnum.ERROR;
      this.error = error instanceof Error ? error.message : "Unknown error";
      throw error;
    }
  }

  /**
   * Send the message based on its type
   */
  async send(): Promise<this> {
    switch (this.type) {
      case PhoneMessageTypeEnum.SMS:
        return await this.sendSms();
      case PhoneMessageTypeEnum.WHATSAPP:
        return await this.sendWhatsApp();
      default:
        throw new Error(`Unsupported phone message type: ${this.type}`);
    }
  }

  /**
   * Create activity log for phone message sending
   */
  private async createActivityLog(): Promise<void> {
    for (const ref of this.refs ?? []) {
      ActivityLogDataAccessLayer.set(
        new ActivityLog({
          id: ref.refType + this.id,
          partnerId: this.partnerId,
          source: { refType: ref.refType, refId: ref.refId },
          target: [{ refType: BackendResourceEnum.MESSAGE, refId: this.id }],
          actionType: ActionEnum.CREATE,
          activity: `PHONE_MESSAGE_${this.type.toUpperCase()}`,
          userId: UserModule.userId,
          timestamp: new TimestampDocument(this.timestamp)
        })
      );
    }
  }

  /**
   * Navigate to phone message detail view
   */
  async goToDetail(router: VueRouter) {
    // This would need to be implemented when phone message detail views are created
    console.warn("Phone message detail navigation not yet implemented");
  }

  /**
   * Create a phone message from SMS DTO
   */
  static fromSmsDto(partnerId: string, data: MrfiktivSendSmsDtoGen, refs?: IReference[]): PhoneMessageBase {
    return new PhoneMessageBase({
      partnerId,
      type: PhoneMessageTypeEnum.SMS,
      identifier: data.identifier,
      to: data.to,
      body: data.body,
      refs: refs || [],
      timestamp: new Timestamp()
    });
  }

  /**
   * Create a phone message from WhatsApp DTO
   */
  static fromWhatsAppDto(partnerId: string, data: MrfiktivSendWhatsAppDtoGen, refs?: IReference[]): PhoneMessageBase {
    return new PhoneMessageBase({
      partnerId,
      type: PhoneMessageTypeEnum.WHATSAPP,
      identifier: data.identifier,
      to: data.to,
      body: data.body,
      refs: refs || [],
      timestamp: new Timestamp()
    });
  }
}

type IPhoneMessage = PhoneMessageBase;
const PhoneMessage = Filter.createForClass(PhoneMessageBase);

export { IPhoneMessage, PhoneMessage };
